import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams} from 'react-router-dom';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import VideoEditorUI from '../view/VideoEditorUI';
import LibraryRepository, {
  EditChunkOption,
} from '../../../data/repositories/LibraryRepository';
import VideoEditorStore, {VideoEditorState} from '../store/VideoEditorStore';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {toast} from 'react-toastify';
import {BrowseStoryblocks} from '../../../modules/BrowseStoryblocks';

import MyLibraryModalStore from '../../../modules/MyLibraryModal/store/MyLibraryModalStore';
import {
  MediaFile,
  MEDIA_FILES_ALL_VIDEO,
} from '../../../data/models/LibraryFile/MediaFile';

import {BrowseMediaLibrary} from '../../../modules/BrowseMediaLibrary/';
import EditNarrationModal from '../components/EditNarrationModal';
import {VideoEditorText} from '../VideoEditorText';

interface Props {
  videoEditorStore?: VideoEditorStore;
  myLibraryModalStore?: MyLibraryModalStore;
}

const EditVideoSourceContainer = inject(
  'videoEditorStore',
  'myLibraryModalStore',
)(
  observer(({videoEditorStore, myLibraryModalStore}: Props) => {
    const [chunks, setChunks] = useState<GeneratedVideoChunk[]>([]);
    const [selectedChunk, setSelectedChunk] = useState<
      GeneratedVideoChunk | undefined
    >();
    const [
      showBrowseStoryblocksForChunkId,
      setShowBrowseStoryblocksForChunkId,
    ] = useState<string | null>(null);

    const [showBrowseLibraryForChunkId, setShowBrowseLibraryForChunkId] =
      useState<string | null>(null);
    const [targetChunkId, setTargetChunkId] = useState<string | null>(null);
    const [libraryDurationFilter, setLibraryDurationFilter] = useState<
      number | null
    >(null);

    // Edit narration modal state
    const [editNarrationChunkId, setEditNarrationChunkId] = useState<
      string | null
    >(null);
    const [editNarrationText, setEditNarrationText] = useState('');
    const [isNarrationLoading, setIsNarrationLoading] = useState(false);
    const [isNarrationSaving, setIsNarrationSaving] = useState(false);

    const {organizationId, mediaFileId} = useParams();
    const [spinTrigger, setSpinTrigger] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
      fetchChunks();
      if (videoEditorStore!.state === VideoEditorState.Idle) {
        return;
      }
      const interval = setInterval(() => {
        fetchChunks();
        setSpinTrigger(prev => prev + 1);
      }, 10000);
      return () => clearInterval(interval);
    }, [mediaFileId, videoEditorStore!.state]);

    const fetchChunks = () => {
      if (organizationId && mediaFileId) {
        videoEditorStore!
          .getVideoChunks(organizationId, mediaFileId)
          .then(chs => {
            const sorted = chs.sort((x, y) => (x.order > y.order ? 1 : -1));
            setChunks(sorted);
            selectFirstOrNextChunkIfSelectedDeleted(sorted);
          })
          .catch((error: Error) => {
            toast.error(error.message, toastConfig);
          });
      }
    };

    // Helper to extract base URL without query parameters
    const getBaseUrl = (url: string | undefined): string => {
      if (!url) return '';
      const idx = url.indexOf('?');
      return idx === -1 ? url : url.substring(0, idx);
    };

    const selectFirstOrNextChunkIfSelectedDeleted = (
      updated: GeneratedVideoChunk[],
    ) => {
      if (selectedChunk) {
        const currentChunk = updated.find(x => x.id === selectedChunk.id);

        if (!currentChunk) {
          // Chunk was deleted - select next or first
          const idx = chunks.findIndex(x => x.id === selectedChunk.id);
          const nextIncluded = updated.slice(idx + 1).find(x => x.include);
          const firstIncluded = updated.find(x => x.include);
          setSelectedChunk(nextIncluded || firstIncluded || undefined);
        } else if (!currentChunk.include) {
          // Chunk is excluded - select next or first included
          const idx = updated.findIndex(x => x.id === selectedChunk.id);
          const nextIncluded = updated.slice(idx + 1).find(x => x.include);
          const firstIncluded = updated.find(x => x.include);
          setSelectedChunk(nextIncluded || firstIncluded || undefined);
        } else {
          // Chunk still exists and is included - update only if URI changed
          const oldBaseUri = getBaseUrl(selectedChunk.uri);
          const newBaseUri = getBaseUrl(currentChunk.uri);
          if (oldBaseUri !== newBaseUri) {
            setSelectedChunk(currentChunk);
          }
        }
      } else if (updated.length > 0) {
        const first = updated.find(x => x.include);
        if (first) setSelectedChunk(first);
      }
    };

    const onBack = () => {
      navigate(`/manage/${organizationId}/library`);
    };

    const openLibraryModal = () => {
      if (!myLibraryModalStore) return;

      (myLibraryModalStore as any).setAllowedTypes?.(MEDIA_FILES_ALL_VIDEO);
      (myLibraryModalStore as any).setOnConfirm?.(onLibraryConfirm);
      (myLibraryModalStore as any).setOnCancel?.(onLibraryCancel);

      if ((myLibraryModalStore as any).open)
        (myLibraryModalStore as any).open({multi: false});
      else if ((myLibraryModalStore as any).show)
        (myLibraryModalStore as any).show({multi: false});
      else (myLibraryModalStore as any).setIsOpen?.(true);
    };

    const closeLibraryModal = () => {
      if (!myLibraryModalStore) return;
      (myLibraryModalStore as any).close?.();
      (myLibraryModalStore as any).hide?.();
      (myLibraryModalStore as any).setIsOpen?.(false);
    };

    const onLibraryConfirm = async (selected: MediaFile | MediaFile[]) => {
      try {
        if (!organizationId || !mediaFileId || !targetChunkId) return;
        const picked: MediaFile = Array.isArray(selected)
          ? selected[0]
          : selected;

        const ok = await videoEditorStore!.EditChunk(
          organizationId,
          mediaFileId,
          EditChunkOption.ChangeFromLibrary,
          targetChunkId,
          {libraryMediaFileId: picked.id},
        );

        if (ok) await fetchChunks();
      } finally {
        closeLibraryModal();
        setShowBrowseLibraryForChunkId(null);
        setTargetChunkId(null);
        setLibraryDurationFilter(null);
      }
    };

    const onLibraryCancel = () => {
      closeLibraryModal();
      setShowBrowseLibraryForChunkId(null);
      setTargetChunkId(null);
      setLibraryDurationFilter(null);
    };

    const onEditChunk = async (option: EditChunkOption, chunkId: string) => {
      if (!organizationId || !mediaFileId) return;

      // Block new operations if another operation is in progress
      if (videoEditorStore!.state !== VideoEditorState.Idle) {
        return;
      }

      if (option === EditChunkOption.ChangeMedia) {
        setShowBrowseStoryblocksForChunkId(chunkId);
        return;
      }

      if (option === EditChunkOption.ChangeFromLibrary) {
        const chunkToEdit = chunks.find(c => c.id === chunkId);
        setLibraryDurationFilter(chunkToEdit?.duration ?? null);
        setTargetChunkId(chunkId);
        setShowBrowseLibraryForChunkId(chunkId);
        openLibraryModal();
        return;
      }

      if (option === EditChunkOption.EditNarration) {
        const chunkToEdit = chunks.find(c => c.id === chunkId);
        if (!chunkToEdit?.txtUri) {
          toast.error(
            'No narration text available for this chunk',
            toastConfig,
          );
          return;
        }

        setEditNarrationChunkId(chunkId);
        setIsNarrationLoading(true);

        try {
          const libraryRepository = new LibraryRepository();
          const response = await libraryRepository.getChunkNarration(
            organizationId,
            mediaFileId,
            chunkId,
          );
          if (!response.success || response.data === undefined) {
            throw new Error('Failed to fetch narration text');
          }
          setEditNarrationText(response.data);
        } catch (error) {
          toast.error('Failed to load narration text', toastConfig);
          setEditNarrationChunkId(null);
        } finally {
          setIsNarrationLoading(false);
        }
        return;
      }

      const ok = await videoEditorStore!.EditChunk(
        organizationId,
        mediaFileId,
        option,
        chunkId,
      );
      if (ok) fetchChunks();
    };

    const onSelectStoryblocksMedia = (
      storyBlocksVideoId: string,
      chunkId: string,
    ) => {
      videoEditorStore!
        .EditChunk(
          organizationId!,
          mediaFileId!,
          EditChunkOption.ChangeMedia,
          chunkId,
          {storyBlocksVideoId},
        )
        .then(result => {
          if (result) fetchChunks();
        });
    };

    const onChunksReorder = (
      chs: GeneratedVideoChunk[],
      activeChunkId: string,
      newIndex: number,
    ) => {
      const newChunks = chs.map((chunk, index) => ({...chunk, order: index}));
      if (organizationId && mediaFileId) {
        videoEditorStore!
          .EditChunk(
            organizationId,
            mediaFileId,
            EditChunkOption.Reorder,
            activeChunkId,
            {newOrder: newIndex},
          )
          .then(result => {
            if (result) fetchChunks();
            setChunks([...newChunks]);
          });
      }
    };

    const revertUnsavedChanges = () =>
      videoEditorStore!
        .revertChanges(organizationId!, mediaFileId!)
        .then(() => fetchChunks());

    const saveUnsavedChanges = () =>
      videoEditorStore!
        .saveChanges(organizationId!, mediaFileId!)
        .then(() => fetchChunks());

    const onNarrationSave = async (newText: string) => {
      if (!editNarrationChunkId || !organizationId || !mediaFileId) return;

      // Keep reference to currently selected chunk to restore after refresh
      const currentSelectedChunkId = selectedChunk?.id;

      setIsNarrationSaving(true);
      try {
        const success = await videoEditorStore!.updateNarration(
          organizationId,
          mediaFileId,
          editNarrationChunkId,
          newText,
        );

        if (success) {
          toast.success(VideoEditorText.editNarrationSuccess, toastConfig);
        }
        setEditNarrationChunkId(null);
        setEditNarrationText('');

        // Refresh chunks and restore selection to the same chunk
        const updatedChunks = await videoEditorStore!.getVideoChunks(
          organizationId,
          mediaFileId,
        );
        const sorted = updatedChunks.sort((x, y) =>
          x.order > y.order ? 1 : -1,
        );
        setChunks(sorted);

        // Restore selection to the same chunk
        if (currentSelectedChunkId) {
          const sameChunk = sorted.find(c => c.id === currentSelectedChunkId);
          if (sameChunk) {
            setSelectedChunk(sameChunk);
          }
        }
      } catch (error) {
        toast.error('Failed to save narration', toastConfig);
      } finally {
        setIsNarrationSaving(false);
      }
    };

    const onNarrationCancel = () => {
      setEditNarrationChunkId(null);
      setEditNarrationText('');
    };

    return !mediaFileId ? null : (
      <>
        <VideoEditorUI
          state={videoEditorStore!.state}
          hasUnsavedChanges={chunks.some(x => x.isDraft)}
          onRevertUnsavedChanges={revertUnsavedChanges}
          onSaveUnsavedChanges={saveUnsavedChanges}
          onBack={onBack}
          chunks={chunks}
          selectedChunk={selectedChunk}
          isLoading={videoEditorStore!.isLoading}
          spinTrigger={spinTrigger}
          onEditChunk={onEditChunk}
          refreshChunks={fetchChunks}
          onChunksReorder={onChunksReorder}
          editChunkErrorCode={videoEditorStore!.editChunkErrorCode}
          clearErrors={() => (videoEditorStore!.editChunkErrorCode = '')}
          onSelectChunk={setSelectedChunk}
        />

        {showBrowseStoryblocksForChunkId && (
          <BrowseStoryblocks
            chunkId={showBrowseStoryblocksForChunkId}
            onCancel={() => setShowBrowseStoryblocksForChunkId(null)}
            onSelect={(storyBlocksVideoId: string, chunkId: string) =>
              onSelectStoryblocksMedia(storyBlocksVideoId, chunkId)
            }
          />
        )}

        {showBrowseLibraryForChunkId && (
          <BrowseMediaLibrary
            organizationId={organizationId}
            onCancel={() => {
              setShowBrowseLibraryForChunkId(null);
              setLibraryDurationFilter(null);
            }}
            onSelect={async (file: MediaFile) => {
              if (
                !organizationId ||
                !mediaFileId ||
                !showBrowseLibraryForChunkId
              )
                return;

              const libraryRepository = new LibraryRepository();

              const mime = (file.fileType || '').toLowerCase();
              const isImage = mime.includes('image');
              const isVideo = mime.includes('video');

              if (!isImage && !isVideo) {
                alert('Please select an image or video from Library.');
                return;
              }

              const res = await libraryRepository.changeChunkToLibraryMedia(
                organizationId,
                mediaFileId,
                showBrowseLibraryForChunkId,
                file.id,
                isImage,
              );

              setShowBrowseLibraryForChunkId(null);
              setLibraryDurationFilter(null);

              if (res?.success) {
                fetchChunks();
              } else if (res?.errors?.length) {
                console.error('changeChunkToLibraryMedia error', res.errors);
              }
            }}
            mode="media"
            minDurationSeconds={libraryDurationFilter ?? undefined}
          />
        )}

        <EditNarrationModal
          shown={editNarrationChunkId !== null}
          initialText={editNarrationText}
          onSave={onNarrationSave}
          onCancel={onNarrationCancel}
          isLoading={isNarrationSaving}
          isTextLoading={isNarrationLoading}
        />
      </>
    );
  }),
);

export default EditVideoSourceContainer;
